import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { apiClient } from '../../../services/apiClient';

const SalesModal = ({ open, onClose, onSubmit }) => {

    const [customers, setCustomers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [loadingSuppliers, setLoadingSuppliers] = useState(false);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [loadingBranches, setLoadingBranches] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [selectedSupplier, setSelectedSupplier] = useState('');
    const [saleDate, setSaleDate] = useState(new Date().toISOString().slice(0, 10));
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState('');
    const [status, setStatus] = useState('completed');
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [paymentNote, setPaymentNote] = useState('');
    const [note, setNote] = useState('');
    const [orderTax, setOrderTax] = useState(0);
    const [discount, setDiscount] = useState(0);
    const [shipping, setShipping] = useState(0);
    const [error, setError] = useState('');

    const resetForm = () => {
        setSelectedCustomer('');
        setSelectedSupplier('');
        setSaleDate(new Date().toISOString().slice(0, 10));
        setSelectedItems([]);
        setSelectedBranch('');
        setStatus('completed');
        setPaymentAmount('');
        setPaymentMethod('cash');
        setPaymentNote('');
        setNote('');
        setOrderTax(0);
        setDiscount(0);
        setShipping(0);
        setError('');
    };

    useEffect(() => {
        if (!open) return;
        const fetchAllData = async () => {
            // Fetch Branches
            setLoadingBranches(true);
            try {
                const { data } = await apiClient.get('/get-all-branch');
                setBranches(data || []);
            } catch {
                setError('Failed to load branches');
            } finally {
                setLoadingBranches(false);
            }

            // Fetch Customers
            setLoadingCustomers(true);
            try {
                const { data } = await apiClient.get('/get-all-customer');
                setCustomers(data || []);
            } catch {
                setError('Failed to load customers');
            } finally {
                setLoadingCustomers(false);
            }

            // Fetch Suppliers
            setLoadingSuppliers(true);
            try {
                const { data } = await apiClient.get('/get-all-supplier');
                setSuppliers(data || []);
            } catch {
                setError('Failed to load suppliers');
            } finally {
                setLoadingSuppliers(false);
            }

            // Fetch Products
            setLoadingProducts(true);
            try {
                const { data: raw } = await apiClient.get('/get-all-products');
                const flattened = raw.flatMap(prod =>
                    prod.product_type === 'variable' && Array.isArray(prod.variants) ? prod.variants.map(v => ({
                        productId: prod.id,
                        variantId: v.id,
                        name: `${prod.name} (${v.variant_value})`,
                        price: parseFloat(v.price || prod.price || 0),
                    })) : [{
                        productId: prod.id,
                        variantId: null,
                        name: prod.name,
                        price: parseFloat(prod.price || 0),
                    }]
                );
                setProductOptions(flattened.map(p => ({
                    value: `${p.productId}_${p.variantId || ''}`,
                    label: p.name,
                    meta: p,
                })));
            } catch {
                setError('Failed to load products');
            } finally {
                setLoadingProducts(false);
            }

        };

        fetchAllData();
    }, [open]);

    const handleProductChange = selected => {
        const unique = (selected || []).map(opt => opt.meta).filter((it, idx, arr) =>
            arr.findIndex(x => x.productId === it.productId && x.variantId === it.variantId) === idx
        );
        setSelectedItems(unique.map(item => ({
            ...item,
            quantity: 1,
            discount: 0,
            taxPercent: 0,
            taxAmount: 0,
        })));
    };

    const calculateGrandTotal = () => {
        const subtotal = selectedItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
        return subtotal + orderTax - discount + shipping;
    };

    const updateQty = (index, newQty) => {
        if (newQty < 1) return;
        setSelectedItems(prev => {
            const items = [...prev];
            items[index] = { ...items[index], quantity: newQty };
            return items;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedBranch) return setError('Please select a branch');
        if (selectedItems.length === 0) return setError('Please add at least one product');
        if (!paymentAmount || isNaN(parseFloat(paymentAmount))) return setError('Please enter a valid payment amount');

        const payload = {
            branch_id: parseInt(selectedBranch),
            customer_id: parseInt(selectedCustomer),
            status: 'completed',
            sale_date: saleDate,
            items: selectedItems.map(it => ({
                product_id: it.productId,
                ...(it.variantId && { variant_id: it.variantId }),
                quantity: it.quantity,
                price: it.price,
            })),
            payment: {
                amount: parseFloat(paymentAmount),
                method: paymentMethod,
                note: paymentNote,
            },
            note,
        };

        try {
            await onSubmit(payload);
            resetForm();
            onClose();
        } catch (err) {
            let message = "Failed to place order. Try again.";
            if (err?.message) {
                message = err.message;
            } else if (typeof err === 'string') {
                message = err;
            }
            setError(message);
        }

    };

    return (
        <Modal show={open} onHide={onClose} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>Add Sale</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}

                    <div className="card border-0">
                        <div className="card-body pb-0">
                            <div className="table-responsive no-pagination mb-3">
                                <table className="table datanew">
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>Qty</th>
                                            <th>Price</th>
                                            <th>Discount</th>
                                            <th>Tax%</th>
                                            <th>Tax Amt</th>
                                            <th>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedItems.map((it, idx) => (
                                            <tr key={idx}>
                                                <td>{it.name}</td>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-secondary"
                                                            onClick={() => updateQty(idx, it.quantity - 1)}
                                                            disabled={it.quantity <= 1}
                                                        >
                                                            -
                                                        </Button>
                                                        <span className="mx-2">{it.quantity}</span>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-primary"
                                                            onClick={() => updateQty(idx, it.quantity + 1)}
                                                        >
                                                            +
                                                        </Button>
                                                    </div>
                                                </td>
                                                <td>PKR {it.price.toFixed(2)}</td>
                                                <td>PKR {it.discount.toFixed(2)}</td>
                                                <td>{it.taxPercent}%</td>
                                                <td>PKR {it.taxAmount.toFixed(2)}</td>
                                                <td>PKR {(it.price * it.quantity).toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Form Fields */}
                            <div className="row">
                                <div className="col-lg-6 col-sm-6 col-12">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Branch</Form.Label>
                                        {loadingBranches ? (
                                            <Spinner size="sm" animation="border" />
                                        ) : (
                                            <Form.Select
                                                value={selectedBranch}
                                                onChange={e => setSelectedBranch(e.target.value)}
                                            >
                                                <option value="">Select Branch</option>
                                                {branches.map(b => (
                                                    <option key={b.id} value={b.id}>{b.name}</option>
                                                ))}
                                            </Form.Select>
                                        )}
                                    </Form.Group>
                                </div>
                                <div className="col-lg-6 col-sm-6 col-12">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Customers</Form.Label>
                                        {loadingCustomers ? (
                                            <Spinner size="sm" animation="border" />
                                        ) : (
                                            <Form.Select
                                                value={selectedCustomer}
                                                onChange={e => setSelectedCustomer(e.target.value)}
                                            >
                                                <option value="">Select Customer</option>
                                                {customers.map(c => (
                                                    <option key={c.id} value={c.id}>{c.customer_name}</option>
                                                ))}
                                            </Form.Select>
                                        )}
                                    </Form.Group>
                                </div>
                                <div className="col-lg-6 col-sm-6 col-12">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Supplier</Form.Label>
                                        {loadingSuppliers ? (
                                            <Spinner size="sm" animation="border" />
                                        ) : (
                                            <Form.Select
                                                value={selectedSupplier}
                                                onChange={e => setSelectedSupplier(e.target.value)}
                                            >
                                                <option value="">Select Supplier (optional)</option>
                                                {suppliers.map(s => (
                                                    <option key={s.id} value={s.id}>{s.supplier_name}</option>
                                                ))}
                                            </Form.Select>
                                        )}
                                    </Form.Group>
                                </div>
                                <div className="col-lg-6 col-sm-6 col-12">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={saleDate}
                                            onChange={e => setSaleDate(e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-lg-12 col-sm-6 col-12">
                                    <Form.Group className="mb-3">
                                        {loadingProducts ? (
                                            <Spinner size="sm" animation="border" />
                                        ) : (
                                            <Select
                                                isMulti
                                                options={productOptions}
                                                value={productOptions.filter(opt =>
                                                    selectedItems.some(it =>
                                                        `${it.productId}_${it.variantId || ''}` === opt.value
                                                    )
                                                )}
                                                onChange={handleProductChange}
                                                placeholder="Pick products..."
                                            />
                                        )}
                                    </Form.Group>
                                </div>
                            </div>

                            {/* Dynamic Totals */}
                            <div className="row">
                                <div className="col-lg-6 ms-auto">
                                    <div className="total-order w-100 max-widthauto m-auto mb-4">
                                        <ul className="border-1 rounded-2">
                                            <li className="border-bottom">
                                                <h4 className="border-end">Order Tax</h4>
                                                <h5>PKR {orderTax.toFixed(2)}</h5>

                                            </li>
                                            <li className="border-bottom">
                                                <h4 className="border-end">Discount</h4>
                                                <h5>PKR {discount.toFixed(2)}</h5>
                                            </li>
                                            <li className="border-bottom">
                                                <h4 className="border-end">Shipping</h4>
                                                <h5>PKR {shipping.toFixed(2)}</h5>
                                            </li>
                                            <li className="border-bottom">
                                                <h4 className="border-end">Grand Total</h4>
                                                <h5>PKR {calculateGrandTotal().toFixed(2)}</h5>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            {/* Dynamic Input Fields */}
                            <div className="row">
                                <div className="col-lg-3 col-sm-6 col-12">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Order Tax</Form.Label>
                                        <Form.Control
                                            type="number"
                                            className='no-spinner'
                                            onWheel={(e) => e.target.blur()}
                                            value={orderTax}
                                            onChange={e => setOrderTax(parseFloat(e.target.value) || 0)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-lg-3 col-sm-6 col-12">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Discount</Form.Label>
                                        <Form.Control
                                            type="number"
                                            className='no-spinner'
                                            value={discount}
                                            onChange={e => setDiscount(parseFloat(e.target.value) || 0)}
                                            onWheel={(e) => e.target.blur()}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-lg-3 col-sm-6 col-12">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Shipping</Form.Label>
                                        <Form.Control
                                            type="number"
                                            className='no-spinner'
                                            value={shipping}
                                            onChange={e => setShipping(parseFloat(e.target.value) || 0)}
                                            onWheel={(e) => e.target.blur()}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-lg-3 col-sm-6 col-12">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select
                                            value={status}
                                            onChange={e => setStatus(e.target.value)}
                                        >
                                            <option value="completed">Completed</option>
                                            <option value="inprogress">In Progress</option>
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                            </div>

                            <hr />

                            {/* Payment Details */}
                            <h5 className="mb-3">Payment Details</h5>
                            <div className="row">
                                <div className="col-md-4">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Payment Method</Form.Label>
                                        <Form.Select
                                            value={paymentMethod}
                                            onChange={e => setPaymentMethod(e.target.value)}
                                        >
                                            <option value="cash">Cash</option>
                                            <option value="card">Card</option>
                                            <option value="points">Points</option>
                                            <option value="deposit">Deposit</option>
                                            <option value="cheque">Cheque</option>
                                            <option value="giftcard">Gift Card</option>
                                            <option value="scan">Scan</option>
                                            <option value="paylater">Pay Later</option>
                                            <option value="external">External</option>
                                            <option value="split">Split Bill</option>
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Payment Amount</Form.Label>
                                        <Form.Control
                                            type="number"
                                            className='no-spinner'
                                            value={paymentAmount}
                                            onChange={e => setPaymentAmount(e.target.value)}
                                            placeholder="Enter payment amount"
                                            onWheel={(e) => e.target.blur()}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Payment Note</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={paymentNote}
                                            onChange={e => setPaymentNote(e.target.value)}
                                            placeholder="Enter payment note"
                                        />
                                    </Form.Group>
                                </div>
                            </div>

                            {/* Note Field */}
                            <Form.Group className="mb-3">
                                <Form.Label>Note</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={note}
                                    onChange={e => setNote(e.target.value)}
                                    placeholder="Enter any additional notes"
                                />
                            </Form.Group>
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" type="submit">Submit</Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

SalesModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
};

export default SalesModal;